import Point from "@arcgis/core/geometry/Point";
import {
  addressToLocations,
  locationToAddress,
  suggestLocations,
} from "@arcgis/core/rest/locator";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { SettingsApiTypes } from "@stellar/settings-api-client";

/** Minimum valid value for latitude */
export const LATITUDE_MIN = -90;

/** Maximum valid value for latitude */
export const LATITUDE_MAX = 90;

/** Minimum valid value for longitude */
export const LONGITUDE_MIN = -180;

/** Maximum valid value for longitude */
export const LONGITUDE_MAX = 180;

/** Number of decimal places kept for latitude/longitude in the deep link. */
export const COORDINATE_PRECISION = 5;

/** Base URL for the ArcGIS geocoding service */
const ARCGIS_GEOCODE_SERVICE_URL =
  "https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer";

/**
 * Language codes used by ArcGIS geocoding service.
 * ArcGIS service supports ISO 3166-1 alpha-2 language codes. More codes can be added if needed.
 * https://developers.arcgis.com/rest/geocode/geocode-coverage/#supported-language-codes
 */
export const ARCGIS_LANGUAGE_CODES = [
  "ES",
  "DE",
  "JA",
  "FR",
  "KO",
  "EN",
  "PT",
  "IT",
  "ZH",
] as const;

/** Represents the language codes supported by the ArcGIS geocoding service */
type ArcGISLanguageCodes = (typeof ARCGIS_LANGUAGE_CODES)[number];

/** Map of language codes supported by our application to the corresponding codes used by ArcGIS service */
export const ARCGIS_LANGUAGE_CODES_MAP: Record<
  SettingsApiTypes.SupportedLanguages,
  ArcGISLanguageCodes
> = {
  es: "ES",
  de: "DE",
  ja: "JA",
  fr: "FR",
  ko: "KO",
  en: "EN",
  pt: "PT",
  it: "IT",
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Name follows a ISO format.
  "zh-Hans": "ZH",
};

/** Represents a suggested address from the geocoding service */
export interface AddressSuggestion {
  /** Readable address for the suggested location */
  label: string;

  /** Along with the label, links a suggestion to an specific location */
  key: string;
}

/** Represents a geocoded address entity */
export interface GeocodedAddress {
  /** Readable address for the geocoded location */
  address: string;

  /** GPS position for the geocoded location */
  position: FaroCoreApiTypes.Position;
}

/** Properties for the getAddressSuggestions function */
interface GetAddressSuggestionsProps {
  /** The input text for which to get address suggestions */
  text: string;

  /** An optional AbortSignal to cancel the request */
  signal?: AbortSignal;
}

/**
 * @returns A list of address suggestions for the given input text using the geocoding service.
 * @throws An error if the request to the geocoding service fails.
 */
export async function getAddressSuggestions({
  text,
  signal,
}: GetAddressSuggestionsProps): Promise<AddressSuggestion[]> {
  const rawSuggestions = await suggestLocations(
    ARCGIS_GEOCODE_SERVICE_URL,
    {
      text,
      // The ArcGIS locator interface has the location field as required but the API endpoint
      // actually has it as optional: https://developers.arcgis.com/rest/geocode/suggest/#location
      // Location is used to bias the suggestions around the pass location, but for our use case
      // we do not want to bias the suggestions around any location, so we leave it as undefined.
      location: undefined as unknown as Point,
    },
    {
      // Prevent suggestion collections from being returned, we only want single suggestions/addresses
      // https://developers.arcgis.com/rest/geocode/suggest/#returncollections
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name given by ArcGIS API
      query: { returnCollections: false },
      signal,
    }
  );

  return rawSuggestions.filter(isValidSuggestionResult).map((suggestion) => ({
    label: suggestion.text,
    key: suggestion.magicKey,
  }));
}

interface GeocodeAddressProps {
  /** The address suggestion to geocode */
  addressSuggestion: AddressSuggestion;

  /** The language code in which to return the geocoded address */
  langCode?: ArcGISLanguageCodes;

  /** An optional AbortSignal to cancel the request */
  signal?: AbortSignal;
}

/**
 * @returns The geocoded address entity for the given address suggestion using the geocoding service.
 * @throws An error if the request to the geocoding service fails or if the response doesn't contain a valid geocoded address.
 */
export async function geocodeAddress({
  addressSuggestion,
  langCode,
  signal,
}: GeocodeAddressProps): Promise<GeocodedAddress> {
  /**
   * This is the workflow suggested by ArcGIS: https://developers.arcgis.com/rest/geocode/find-address-candidates/#magickey
   * 1. Get suggestions from the /suggest endpoint. This is handled by the getAddressSuggestions function.
   * 2. Once the user selects an address suggestion, request its corresponding geocoded address candidate
   * via the /findAddressCandidates endpoint with "magicKey" and "singleLine" parameters.
   */
  const candidates = await addressToLocations(
    ARCGIS_GEOCODE_SERVICE_URL,
    {
      address: { singleLine: addressSuggestion.label },
      magicKey: addressSuggestion.key,
      maxLocations: 1,
    },
    {
      signal,
      query: { langCode },
    }
  );

  const candidate = candidates[0];

  if (!candidate || !isValidAddressCandidate(candidate)) {
    throw new Error(
      "Failed to geocode address suggestion: No valid candidates found"
    );
  }

  return {
    address: candidate.address,
    position: {
      lat: candidate.location.latitude,
      long: candidate.location.longitude,
    },
  };
}

/**
 * Uses the geocoding service to reverse geocode a GPS position into a human-readable address.
 * @returns The readable address for the given GPS position or an empty string if no readable address is found.
 * @param position The GPS position to reverse geocode.
 * @throws An error if the request to the geocoding service fails.
 */
export async function reverseGeocode(
  position: FaroCoreApiTypes.Position
): Promise<string> {
  const point = new Point({
    longitude: position.long,
    latitude: position.lat,
  });

  const response = await locationToAddress(ARCGIS_GEOCODE_SERVICE_URL, {
    location: point,
  });

  const longLabel = response.attributes?.LongLabel;

  // As top priority use the `LongLabel` attribute if defined since it
  // contains more detailed information of the readable address.
  // If not defined then return the `address` field or an empty string if
  // if both `LongLabel` and `address` are not defined or not valid strings.
  if (typeof longLabel === "string") {
    return longLabel;
  }

  return response.address ?? "";
}

/**
 * @returns Whether the given error is an AbortError caused by aborting a geocoding request.
 * @param error The error to check.
 */
export function isGeocodeAbortError(error: unknown): boolean {
  // The geocoding service we use, ArcGIS, returns a custom error object with the name
  // property set to "AbortError" when a request is aborted:
  // https://developers.arcgis.com/javascript/latest/async-cancellation-with-abortcontroller
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    typeof error.name === "string" &&
    error.name === "AbortError"
  );
}

/**
 * @returns Whether the ArcGIS suggestion result has defined text and magicKey properties.
 * @param value The ArcGIS suggestion result to check.
 */
export function isValidSuggestionResult(
  value: __esri.SuggestionResult
): value is __esri.SuggestionResult & { text: string; magicKey: string } {
  return typeof value.text === "string" && typeof value.magicKey === "string";
}

/** An ArcGIS location entity with defined latitude and longitude */
type ValidLocation = Omit<__esri.PointProperties, "latitude" | "longitude"> & {
  /** The latitude of the location */
  latitude: number;

  /** The longitude of the location */
  longitude: number;
};

/**
 * @returns Whether the given ArcGIS address candidate has defined address, latitude and longitude properties.
 * @param value The ArcGIS address candidate to check.
 */
export function isValidAddressCandidate(
  value: __esri.AddressCandidate
): value is __esri.AddressCandidate & {
  address: string;
  location: ValidLocation;
} {
  return (
    typeof value.address === "string" &&
    typeof value.location?.latitude === "number" &&
    typeof value.location?.longitude === "number"
  );
}
